import { useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { copyTextToClipboard, shareURL } from '@tma.js/sdk-react';
import { Film, Share2 } from 'lucide-react';

import { env } from '@/env';
import { useHaptics } from '@/hooks/useHaptics';
import { useGetMatchQuery } from '@/services/qchinesepokerApi';
import type { MatchResultPayload, Seat } from '@/lib/qcp/protocol';

/* ------------------------------------------------------------------ */
/* Types                                                               */
/* ------------------------------------------------------------------ */

/**
 * Router state shape passed by ChinesePokerMatch when the multi-hand set ends.
 *
 * The match-over trigger is now `match:result` (MatchResultPayload) — the
 * canonical end of the multi-hand "set" — NOT the legacy single-hand
 * `match:ended` (EndedPayload). The result screen reads `matchResult` from
 * router location state and falls back to the RTK MatchSummary on a
 * refresh / deep-link.
 */
interface ResultState {
  matchResult: MatchResultPayload;
  mySeat: Seat;
}

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

/** The per-seat final cumulative net for `seat`, taken from the match result. */
function finalNetFor(result: MatchResultPayload, seat: Seat): number {
  return seat === 'A' ? result.finalNetA : result.finalNetB;
}

/** Map the server `reason` enum to a locale key. `forfeit` and `resign` share a label. */
function reasonKey(
  reason: MatchResultPayload['reason'],
):
  | 'result.reasonComplete'
  | 'result.reasonDraw'
  | 'result.reasonResign'
  | 'result.reasonAbandoned' {
  switch (reason) {
    case 'draw':
      return 'result.reasonDraw';
    case 'resign':
    case 'forfeit':
      return 'result.reasonResign';
    case 'abandoned':
      return 'result.reasonAbandoned';
    case 'complete':
    default:
      return 'result.reasonComplete';
  }
}

/** Derive outcome key + colour from the match result relative to mySeat. */
function deriveOutcome(
  result: MatchResultPayload,
  mySeat: Seat,
): { labelKey: 'result.youWon' | 'result.youLost' | 'result.tie'; netLabel: string; badgeClass: string } {
  const net = finalNetFor(result, mySeat);
  const netLabel = net > 0 ? `+${net}` : `${net}`;

  if (result.winner === null) {
    return {
      labelKey: 'result.tie',
      netLabel,
      badgeClass: 'bg-amber-500/20 text-amber-300',
    };
  }
  if (result.winner === mySeat) {
    return {
      labelKey: 'result.youWon',
      netLabel,
      badgeClass: 'bg-emerald-500/20 text-emerald-300',
    };
  }
  return {
    labelKey: 'result.youLost',
    netLabel,
    badgeClass: 'bg-rose-500/20 text-rose-300',
  };
}

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

/**
 * Post-match Chinese Poker result screen (multi-hand "set").
 *
 * Reached from ChinesePokerMatch when `match:result` fires — the match screen
 * navigates here with { matchResult, mySeat } in router location state. The
 * payload carries the final cumulative net (finalNetA / finalNetB), the set
 * winner, the number of hands played (incl. FL extensions), the end reason and
 * the per-seat ELO deltas (null when a bot is involved).
 *
 * Also tolerates a deep-link / page-refresh (state absent): falls back to
 * fetching the MatchSummary via RTK Query and shows a neutral header.
 *
 * Card image: `GET /matches/:id/card.png` is a PUBLIC endpoint on qcp-api
 * (no auth). In dev the Vite proxy rewrites `/qchinesepoker-api/*` to the
 * MacMini at :4100. The img has an onError fallback so a missing/slow card
 * never breaks the screen.
 *
 * Share: `shareURL` from @tma.js/sdk-react opens Telegram's native contact
 * share sheet. Guarded with `shareURL.isAvailable()` so it no-ops outside
 * Telegram (browser dev, refresh outside the WebView). Clipboard fallback
 * mirrors InviteSheet.
 */
export function ChinesePokerResult() {
  const { t } = useTranslation('chinesepoker');
  const { id = '' } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const haptics = useHaptics();

  // ── Router state (present when navigated from ChinesePokerMatch) ────
  const routerState = (location.state as ResultState | null) ?? null;
  const matchResult = routerState?.matchResult ?? null;
  const mySeat = routerState?.mySeat ?? null;

  // ── Fallback: fetch match summary when state is absent (refresh/deep-link)
  const { data: matchSummary } = useGetMatchQuery(id, {
    skip: Boolean(matchResult) || !id,
  });

  // ── Card image state ────────────────────────────────────────────────
  const [imgLoaded, setImgLoaded] = useState(false);
  const [imgError, setImgError] = useState(false);

  // ── Share state ─────────────────────────────────────────────────────
  const [copied, setCopied] = useState(false);

  // ── Derive outcome ──────────────────────────────────────────────────
  const outcome =
    matchResult && mySeat
      ? deriveOutcome(matchResult, mySeat)
      : null;

  // ── ELO delta for this seat (null when a bot is involved) ────────────
  const myEloDelta =
    matchResult && mySeat && matchResult.elo
      ? matchResult.elo[mySeat].delta
      : null;

  // ── Card image URL ─────────────────────────────────────────────────
  // env.qcpApiUrl is '/qchinesepoker-api' in dev (Vite proxy → MacMini :4100)
  // and 'https://qchinesepoker.qlash.gg' in prod.
  const cardUrl = `${env.qcpApiUrl}/matches/${encodeURIComponent(id)}/card.png`;

  // ── Share handler ──────────────────────────────────────────────────
  async function handleShare() {
    haptics.impact('light');
    const botLink = `https://t.me/${env.telegramBotUsername}`;

    // Build the share text using locale keys.
    let text: string;
    if (outcome) {
      text = t('result.shareText', {
        result: t(outcome.labelKey),
        net: outcome.netLabel,
        app: botLink,
      });
    } else {
      text = t('result.shareTextDefault', { app: botLink });
    }

    // shareURL opens Telegram's native share sheet — guarded with the SDK's
    // own isAvailable() signal so it no-ops outside Telegram (browser dev,
    // refresh outside the WebView).
    if (shareURL.isAvailable()) {
      try {
        shareURL(botLink, text);
      } catch {
        /* Not in Telegram — fall through to clipboard */
      }
    }

    // Clipboard fallback: mirrors InviteSheet.tsx's two-level try/catch.
    try {
      await copyTextToClipboard(text);
    } catch {
      try {
        await navigator.clipboard.writeText(text);
      } catch {
        /* clipboard unavailable — silently no-op */
      }
    }

    setCopied(true);
    window.setTimeout(() => setCopied(false), 1_500);
  }

  // ── Render ─────────────────────────────────────────────────────────

  return (
    <div
      className="mx-auto flex max-w-[520px] flex-col items-center gap-6 px-4 py-8"
      style={{ paddingTop: 'calc(var(--tg-viewport-safe-area-inset-top, 0px) + 2rem)' }}
    >
      {/* ── Result header ──────────────────────────────────────────── */}
      <header className="flex w-full flex-col items-center gap-2 rounded-2xl bg-bg-secondary px-6 py-5 text-center">
        {outcome && matchResult ? (
          <>
            <span
              className={`rounded-full px-4 py-1 text-sm font-bold uppercase tracking-widest ${outcome.badgeClass}`}
            >
              {t(outcome.labelKey)}
            </span>
            <p className="text-4xl font-bold tabular-nums text-fg">
              {outcome.netLabel}
            </p>
            <p className="text-xs font-medium text-fg-muted">{t('result.points')}</p>
            <p className="text-xs font-medium text-fg-muted">{t(reasonKey(matchResult.reason))}</p>
          </>
        ) : matchSummary ? (
          <>
            <span className="rounded-full bg-bg-tertiary px-4 py-1 text-sm font-semibold text-fg-muted uppercase tracking-widest">
              {t('result.matchResult')}
            </span>
            <p className="text-4xl font-bold tabular-nums text-fg">
              {matchSummary.net.A} – {matchSummary.net.B}
            </p>
            <p className="text-xs font-medium text-fg-muted">{t('result.seatLabel')}</p>
          </>
        ) : (
          <span className="text-sm font-semibold text-fg-muted">{t('result.matchResult')}</span>
        )}
      </header>

      {/* ── Match summary stats (final score / hands played / ELO) ───── */}
      {matchResult && mySeat && (
        <div className="grid w-full grid-cols-2 gap-3">
          {/* Final cumulative score (both seats, relative to me) */}
          <div className="col-span-2 flex items-center justify-between rounded-2xl bg-bg-secondary px-5 py-4">
            <span className="text-xs font-medium uppercase tracking-wide text-fg-muted">
              {t('result.finalScore')}
            </span>
            <span className="text-lg font-bold tabular-nums text-fg">
              {finalNetFor(matchResult, mySeat)} – {finalNetFor(matchResult, mySeat === 'A' ? 'B' : 'A')}
            </span>
          </div>

          {/* Hands played */}
          <div className="flex flex-col items-center justify-center gap-1 rounded-2xl bg-bg-secondary px-4 py-4 text-center">
            <span className="text-xs font-medium uppercase tracking-wide text-fg-muted">
              {t('result.handsPlayed', { count: matchResult.handsPlayed })}
            </span>
          </div>

          {/* ELO delta — only when present (null with a bot) */}
          <div className="flex flex-col items-center justify-center gap-1 rounded-2xl bg-bg-secondary px-4 py-4 text-center">
            <span className="text-xs font-medium uppercase tracking-wide text-fg-muted">
              {t('result.eloDelta')}
            </span>
            {myEloDelta !== null ? (
              <span
                className={`text-lg font-bold tabular-nums ${
                  myEloDelta > 0
                    ? 'text-emerald-300'
                    : myEloDelta < 0
                      ? 'text-rose-300'
                      : 'text-fg'
                }`}
              >
                {myEloDelta > 0 ? `+${myEloDelta}` : `${myEloDelta}`}
              </span>
            ) : (
              <span className="text-lg font-bold text-fg-muted">—</span>
            )}
          </div>
        </div>
      )}

      {/* ── Share card image ─────────────────────────────────────────── */}
      <div className="w-full overflow-hidden rounded-2xl bg-bg-secondary">
        {/* Skeleton shown until load or on error */}
        {!imgLoaded && !imgError && (
          <div className="flex h-48 w-full animate-pulse items-center justify-center rounded-2xl bg-bg-tertiary">
            <span className="text-xs text-fg-muted">{t('result.cardLoading')}</span>
          </div>
        )}

        {/* The actual PNG — PUBLIC, no auth needed */}
        {!imgError && (
          <img
            src={cardUrl}
            alt="Match result card"
            className={`w-full rounded-2xl object-contain transition-opacity duration-300 ${
              imgLoaded ? 'opacity-100' : 'opacity-0 h-0'
            }`}
            onLoad={() => setImgLoaded(true)}
            onError={() => {
              setImgError(true);
              setImgLoaded(false);
            }}
          />
        )}

        {/* Text fallback when the PNG fails to load */}
        {imgError && outcome && (
          <div className="flex flex-col items-center gap-1 px-4 py-6 text-center">
            <p className="text-sm font-semibold text-fg">
              {t(outcome.labelKey)} {outcome.netLabel} {t('result.points')}
            </p>
            <p className="text-xs text-fg-muted">{t('result.cardUnavailable')}</p>
          </div>
        )}
        {imgError && !outcome && (
          <div className="flex flex-col items-center gap-1 px-4 py-6 text-center">
            <p className="text-xs text-fg-muted">{t('result.cardUnavailable')}</p>
          </div>
        )}
      </div>

      {/* ── Actions ──────────────────────────────────────────────────── */}
      <div className="flex w-full flex-col gap-3">
        {/* Share button */}
        <button
          type="button"
          onClick={handleShare}
          className="flex items-center justify-center gap-2 rounded-xl bg-brand px-6 py-3 text-sm font-semibold text-brand-fg transition-opacity active:opacity-80"
        >
          <Share2 className="h-4 w-4" />
          {copied ? t('result.copied') : t('result.share')}
        </button>

        {/* Replay CTA */}
        <button
          type="button"
          onClick={() => {
            haptics.impact('light');
            navigate(`/chinesepoker/replay/${id}`);
          }}
          className="flex items-center justify-center gap-2 rounded-xl border border-border bg-bg-secondary px-6 py-3 text-sm font-semibold text-fg transition-opacity active:opacity-80"
        >
          <Film className="h-4 w-4" />
          {t('result.viewReplay')}
        </button>

        {/* Play again CTA */}
        <button
          type="button"
          onClick={() => {
            haptics.impact('light');
            navigate('/chinesepoker');
          }}
          className="rounded-xl border border-border bg-bg-secondary px-6 py-3 text-sm font-semibold text-fg transition-opacity active:opacity-80"
        >
          {t('result.playAgain')}
        </button>
      </div>
    </div>
  );
}
